import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RegionalService from '../services/RegionalService';

const hasMultipleRoleContexts = (contexts) => {
  const distinctRoles = new Set(
    (Array.isArray(contexts) ? contexts : [])
      .map((context) => String(context?.nombreRol || context?.NombreRol || '').trim().toLowerCase())
      .filter(Boolean)
  );

  return distinctRoles.size > 1;
};

// Componente de pantalla de carga
const LoadingScreen = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
    color: 'white',
    flexDirection: 'column',
    gap: '1rem'
  }}>
    <div style={{
      width: '60px',
      height: '60px',
      border: '4px solid rgba(255, 255, 255, 0.3)',
      borderTop: '4px solid white',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }}></div>
    <p style={{ fontSize: '1.1rem', fontWeight: '500' }}>
      Verificando autenticación...
    </p>
    <style>
      {`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}
    </style>
  </div>
);

// Componente para proteger rutas
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  const regionales = RegionalService.getRegionales();
  const activeRegional = RegionalService.getActiveRegional();
  const needsRegionalSelection = regionales && regionales.length > 1 && !activeRegional;
  const availableAccessContexts = Array.isArray(user?.availableAccessContexts) ? user.availableAccessContexts : [];
  const needsAccessContextSelection = (Boolean(user?.requiresAccessContextSelection) || hasMultipleRoleContexts(availableAccessContexts))
    && availableAccessContexts.length > 1
    && !user?.currentAccessContext;
  const roleName = (user?.roleName || user?.nombreRol || user?.role || '').toLowerCase();
  const isAdmin = roleName.includes('admin');


  // Mostrar pantalla de carga mientras se verifica la autenticación
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Si no está autenticado, redirigir al login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Si el usuario debe cambiar su contraseña, bloquearlo hasta que lo haga
  if (user?.requirePasswordChange && location.pathname !== '/cambiar-contrasena') {
    return <Navigate to="/cambiar-contrasena" replace />;
  }

  if (needsAccessContextSelection && location.pathname !== '/select-access-context') {
    return <Navigate to="/select-access-context" replace state={{ from: location }} />;
  }

  // Para admin usamos el flujo nuevo, no aplicar la lógica clásica de selección múltiple
  if (!isAdmin && needsRegionalSelection && location.pathname !== '/select-regional') {
    return <Navigate to="/select-regional" replace state={{ from: location }} />;
  }

  // Si está autenticado, mostrar el contenido protegido
  return children;
};

export default ProtectedRoute;
